"use client";

/**
 * Enrollment-preview drawer — the primary review-and-enroll surface.
 *
 * A wide right-side `Sheet` that slides over the current page, driven by
 * the preview store's `open` / `context`. The opener (Configure modal,
 * sequence page, …) calls `usePreviewStore().openDrawer({ jobId,
 * sequenceId, previewSessionId })`; this component mounts the shared
 * `PreviewContent` body inside the Sheet so none of the orchestration is
 * duplicated.
 *
 * Close paths all flow through the same has-overrides discard guard:
 *   - Top-bar X / discard confirm → `PreviewContent` → `closeDrawer()`.
 *   - Esc / backdrop press        → `Sheet.onOpenChange(false)` →
 *     `PreviewContent.requestClose()` (the guarded close).
 *
 * On a successful commit we refresh the credit balance + contacts
 * (`invalidateAfterReveal`), invalidate the enrollments list so the new
 * cohort shows up, then close — no navigation, since a drawer has no route.
 */

import * as React from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Sheet } from "@/components/ui/sheet";
import { getListEnrollmentsQueryKey } from "@/lib/api/generated";

import { invalidateAfterReveal } from "../../lib/invalidate-after-reveal";
import { usePreviewStore } from "../../store/preview-store";

import { PreviewContent, type PreviewContentHandle } from "./preview-content";

export function EnrollmentPreviewDrawer() {
  const open = usePreviewStore((s) => s.open);
  const context = usePreviewStore((s) => s.context);
  const closeDrawer = usePreviewStore((s) => s.closeDrawer);
  const queryClient = useQueryClient();

  const contentRef = React.useRef<PreviewContentHandle>(null);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (next) return;
      // Route Esc / backdrop dismissal through the same has-overrides
      // discard guard the top-bar X uses. Fall back to a hard close if the
      // body isn't mounted (shouldn't happen while open).
      if (contentRef.current) {
        contentRef.current.requestClose();
      } else {
        closeDrawer();
      }
    },
    [closeDrawer],
  );

  const handleCommitted = React.useCallback(() => {
    if (!context) return;
    invalidateAfterReveal(queryClient);
    queryClient.invalidateQueries({
      queryKey: getListEnrollmentsQueryKey(context.jobId, context.sequenceId),
    });
    closeDrawer();
  }, [context, queryClient, closeDrawer]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange} size="profile">
      {open && context ? (
        <PreviewContent
          ref={contentRef}
          jobId={context.jobId}
          sequenceId={context.sequenceId}
          previewSessionId={context.previewSessionId}
          onClose={closeDrawer}
          onCommitted={handleCommitted}
          errorActionLabel="Close"
        />
      ) : null}
    </Sheet>
  );
}

export default EnrollmentPreviewDrawer;
